<template>
  <v-card align="center" justify="center" light min-width="35vw" outlined>
    <v-card-title style="background-color: #d2d7e1">
      <v-list-item>
        <v-list-item-title><h2>{{ $t('title') }}</h2></v-list-item-title>
        <v-spacer/>
        <v-list-item-action>
          <v-icon class="icon" icon @click="close">fa-times</v-icon>
        </v-list-item-action>
      </v-list-item>
    </v-card-title>
    <v-card-text class="px-3 py-7">
      <v-container fluid>
        <v-form v-model="valid">
          <div class="d-flex align-center justify-center mb-3">
            <v-text-field :label="$t('nameLabel')" :placeholder="$t('namePlaceholder', { name: this.$store.state.clubMember.club.name })"
                          v-model="tournamentName" :rules="tournamentNameRules" counter="45" outlined/>
            <v-menu :close-on-content-click="false" offset-y transition="scale-transition" v-model="menuDates">
              <template v-slot:activator="{ on }">
                <v-text-field class="ml-2" :label="$t('datesLabel')" outlined persistent-hint readonly v-model="datesRangeText" :rules="dateRules" v-on="on"/>
              </template>
              <v-date-picker @input="inputDates" range no-title v-model="tournamentDates"/>
            </v-menu>
          </div>
          <v-text-field :label="$t('faqsLabel')" :placeholder="$t('faqsPlaceholder', { name: String(this.$store.state.clubMember.club.name).replaceAll(' ', '').toLowerCase() })"
                        :hint="$t('faqsHint')" persistent-hint
                        v-model="tournamentFAQs" :rules="tournamentFAQsRules" counter="100" outlined/>
          <v-row class="mt-7" align="center" justify="center">
            <v-btn @click="close">{{ $t('btnCancel') }}</v-btn>
            <v-btn :disabled="!this.valid || this.tournamentDates.length !== 2" class="ml-3" @click="editTournament">
              {{ $t('btnEdit') }}</v-btn>
          </v-row>
        </v-form>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
import Repository from '../../../repositories/tournament';
import { mapMutations } from 'vuex';
export default {
  name: "TournamentSettings",
  props: {
    tournamentSummary: Object,
  },
  data() {
    return {
      valid: false,
      tournamentName: '',
      tournamentFAQs: '',
      tournamentDates: [new Date().toISOString().substr(0, 10), new Date().toISOString().substr(0, 10)],
      menuDates: false,
      formalStartDate: null,
      formalEndDate: null,
      tournamentNameRules: [
        t => !!t || 'Name is required',
        t => (t && t.length <= 45) || 'Max 45 characters',
      ],
      dateRules: [
        d => !!d || 'Days are required',
      ],
      tournamentFAQsRules: [
        f => ((f && f.length <= 100 ) || f.length === 0)  || 'Max 100 characters',
      ],
    };
  },
  mounted() {
    this.init();
  },
  computed: {
    datesRangeText () {
      return this.tournamentDates.join(' to ');
    },
  },
  methods: {
    init() {
      this.tournamentName = this.tournamentSummary.tournamentName;
      this.tournamentFAQs = (this.tournamentSummary.faqsUrl !== null)? this.tournamentSummary.faqsUrl : '';
      this.tournamentDates = [new Date(this.tournamentSummary.startTime).toISOString().substr(0, 10), new Date(this.tournamentSummary.endTime).toISOString().substr(0, 10)];
    },
    inputDates(){
      if(this.tournamentDates.length === 2)
        this.menuDates = false;
    },
    ...mapMutations(['setTournament']),
    editTournament() {
      if(this.valid && this.tournamentDates.length === 2) {
        let startDateArray = this.tournamentDates[0].split('-');
        let endDateArray = this.tournamentDates[1].split('-');
        this.formalStartDate = new Date(startDateArray[0], startDateArray[1] - 1, startDateArray[2]);
        this.formalEndDate = new Date(endDateArray[0], endDateArray[1] - 1, endDateArray[2]);

        if(this.formalStartDate >= this.formalEndDate) {
          alert(this.$t('alerts.notBefore'));
          return false;
        }
        else if(new Date() > this.formalStartDate || new Date() > this.formalEndDate) {
          alert(this.$t('alerts.notToday'));
          return false;
        }

        let tournamentEditData = {
          tournamentId: this.tournamentSummary.tournamentId,
          tournamentName: this.tournamentName,
          startTime: this.formalStartDate,
          endTime: this.formalEndDate,
          faqsUrl: this.tournamentFAQs,
        };

        Repository.editFullTournament(tournamentEditData).then((response) => {
          if(response.status < 400 && response.data) {
            alert(this.$t('alerts.edited'));
            Repository.getTournamentOverview(this.tournamentSummary.tournamentId).then((response) => {
              if(response.status < 400)
                this.setTournament(response.data);
            });
            this.close();
          }
          else
            alert('An error occurred :(');
        });
      }
    },
    capitalize(string) {
      return string[0].toUpperCase() + string.slice(1).toLowerCase();
    },
    close() {
      this.$emit('closeWindow');
    },
  }
}
</script>

<i18n>
{
  "en": {
    "title": "Tournament Settings",
    "nameLabel": "Tournament Name",
    "namePlaceholder": "E.g. January {name} Tournament",
    "datesLabel": "Tournament Dates",
    "faqsLabel": "Tournament FAQs (Optional)",
    "faqsPlaceholder": "E.g. https://{name}.com/tournament",
    "faqsHint": "Webpage with the most important answered questions about the tournament.",
    "alerts": {
      "notToday": "Tournament can't be edited.\nDates SHOULD be after today.",
      "notBefore": "Tournament can't be edited.\nStart date SHOULD be before the End date.",
      "edited": "Tournament was edited successfully!"
    },
    "btnCancel": "Cancel",
    "btnEdit": "Edit Tournament"
  },
  "es": {
    "title": "Ajustes del Torneo",
    "nameLabel": "Nombre del Torneo",
    "namePlaceholder": "Ejemplo: Torneo de Enero {name}",
    "datesLabel": "Fechas del Torneo",
    "faqsLabel": "FAQs del Torneo (Opcional)",
    "faqsPlaceholder": "Ejemplo: https://{name}.com/torneo",
    "faqsHint": "Página web con las preguntas respondidas más importantes acerca del torneo.",
    "alerts": {
      "notToday": "Torneo no puede ser editado.\nFechas DEBEN ser luego del día de hoy.",
      "notBefore": "Torneo no puede ser editado.\nFecha de Inicio DEBE ser antes de la Fecha de Fin.",
      "edited": "¡Torneo editado satisfactoriamente!"
    },
    "btnCancel": "Cancelar",
    "btnEdit": "Editar Torneo"
  }
}
</i18n>

<style scoped>

</style>
